import type { PermissionRequest, RequestCreate, RequestUpdate } from '@gestion-permisos/contracts';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { Card, CardContent } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { EMPTY_REQUEST, RequestForm } from '../requests/request-form.js';
import { type CreateInput, useCreateAndSubmitRequest, useCreateRequest, useRequest, useUpdateRequest } from '../requests/requests-api.js';
import { submitFailureState } from '../requests/submit-after-create.js';
import { useMe } from '../session/session.js';

/** 403 o 404 de la API reemplazan la pantalla (`ApiErrorView`); los demás errores se muestran en el formulario. */
const replacesPage = (error: unknown) => error instanceof ApiProblem && (error.status === 403 || error.status === 404);

/** Frase de la cabecera, la de la Power App (`src/pages/new-request-page.tsx`). */
const DESCRIPTION = 'Registra tu ausencia. El empleado, aprobador, duración y estado se gestionan automáticamente.';

/** Contenedor con el aire de las pantallas ya adaptadas (inicio, Mis solicitudes y detalle). */
const PAGE = 'space-y-7 lg:px-6 lg:py-4';

/**
 * Nueva solicitud (W3): «Guardar borrador» (`POST /requests` con `Idempotency-Key`) y «Enviar para aprobación»,
 * como en la Power App (`src/pages/new-request-page.tsx`): el alta y, con su 201, `POST /requests/{id}/submit`.
 * Con un documento elegido, los dos suben el archivo tras el alta (`POST /requests/{id}/documents`) y antes de
 * enviar. Si el alta falla, el error se queda en el formulario. Si el borrador se creó y falló el documento o
 * el envío, se va a su detalle con el resultado en el estado de la navegación: quedarse aquí y reintentar
 * crearía otro borrador. Ocultar el enlace a quien no tiene `canRequest` no es la protección: un 403 se
 * muestra con `ApiErrorView`.
 */
export function NewRequestPage() {
  const navigate = useNavigate();
  const create = useCreateRequest();
  const send = useCreateAndSubmitRequest();
  // Cerrojo síncrono: una pulsación repetida antes de que se pinten los botones desactivados no crea otra
  // solicitud. Se libera cuando termina la operación (con éxito se navega; con error se puede reintentar).
  const busy = useRef(false);
  const run = (mutation: typeof create, other: typeof create, input: CreateInput) => {
    if (busy.current) return;
    busy.current = true;
    other.reset();
    mutation.mutate(input, {
      onSuccess: ({ request, failure }) =>
        void navigate(`/solicitudes/${request.id}`, failure ? { state: submitFailureState(failure.step, request.version, failure.error) } : undefined),
      onSettled: () => {
        busy.current = false;
      },
    });
  };
  const error = create.error ?? send.error;
  if (replacesPage(error)) return <ApiErrorView error={error} />;
  return (
    <div className={PAGE}>
      <PageHeader title="Nueva solicitud" description={DESCRIPTION} />
      <RequestForm
        initial={EMPTY_REQUEST}
        submitLabel="Guardar borrador"
        pending={create.isPending || send.isPending}
        error={error}
        onSubmit={(body, file) => run(create, send, { body, file })}
        onSend={(body, file) => run(send, create, { body, file })}
      />
    </div>
  );
}

const NOT_A_REQUEST = new ApiProblem(404, 'No encontrada', 'NOT_FOUND', undefined, undefined, []);

/** Editar el borrador propio (W3): `PATCH /requests/{id}` con `If-Match` y solo los campos cambiados. */
export function EditRequestPage() {
  const { id = '' } = useParams();
  if (!z.uuid().safeParse(id).success) return <ApiErrorView error={NOT_A_REQUEST} />;
  return <EditRequest id={id} />;
}

function EditRequest({ id }: { id: string }) {
  const me = useMe();
  const navigate = useNavigate();
  const request = useRequest(id);
  const update = useUpdateRequest(id);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const data = request.data;
  // Valores del formulario ligados a la versión: tras un 412 llega otra versión y el formulario se recarga.
  const initial = useMemo(() => (data ? valuesOf(data) : undefined), [data]);

  if (request.isError) return <ApiErrorView error={request.error} />;
  if (!data || !initial) return <output className="block text-muted-foreground">Cargando…</output>;
  if (replacesPage(update.error)) return <ApiErrorView error={update.error} />;
  if (data.status !== 'DRAFT' || data.employeeId !== me.id) {
    return (
      <div className={PAGE}>
        <PageHeader title={`Editar ${data.requestNumber}`} description={DESCRIPTION} />
        <Card>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">Solo se puede editar un borrador propio.</p>
            <Link to={`/solicitudes/${id}`} className="inline-block text-primary underline underline-offset-4">
              Ver la solicitud
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = (values: RequestCreate) => {
    const changes = changedFields(initial, values);
    if (Object.keys(changes).length === 0) {
      setNotice('No hay cambios que guardar.');
      return;
    }
    setNotice(undefined);
    update.mutate(
      { version: data.version, changes },
      {
        onSuccess: (updated) => void navigate(`/solicitudes/${updated.id}`),
        onError: (error) => {
          if (error instanceof ApiProblem && error.status === 412) {
            setNotice('Otra persona cambió la solicitud mientras la editabas. Se cargaron los datos actuales: revísalos y vuelve a guardar.');
          }
        },
      },
    );
  };

  return (
    <div className={PAGE}>
      <Link to={`/solicitudes/${id}`} className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Volver a la solicitud
      </Link>
      <PageHeader title={`Editar ${data.requestNumber}`} description={DESCRIPTION} />
      <RequestForm initial={initial} submitLabel="Guardar cambios" pending={update.isPending} error={update.error} notice={notice} onSubmit={onSubmit} />
    </div>
  );
}

function valuesOf(request: PermissionRequest): RequestCreate {
  return {
    permissionTypeId: request.permissionTypeId,
    permissionDate: request.permissionDate,
    departureTime: request.departureTime,
    returnTime: request.returnTime,
    reason: request.reason,
  };
}

/** Solo los campos que cambian (el `PATCH` exige al menos uno). */
function changedFields(before: RequestCreate, after: RequestCreate): RequestUpdate {
  const changes: Record<string, string> = {};
  for (const key of Object.keys(after) as (keyof RequestCreate)[]) {
    if (after[key] !== before[key]) changes[key] = after[key];
  }
  return changes as RequestUpdate;
}
